import { Component, OnInit, inject, signal, effect, OnDestroy } from '@angular/core';
import { StakeInfo } from '../../models/common.interface';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { StakingService } from '../../services/staking.service';
import { Web3Service } from '../../services/web3.service';
import { ToastService } from '../../services/toast.service';
import { ModalService } from '../../services/modal.service';
import { getAccount, readContract } from '@wagmi/core';
import { CONTRACT_ADDRESSES, TOKEN_ABI } from '../../constants/contracts';
import { formatUnits, parseUnits } from 'viem';


@Component({
  selector: 'app-staking-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './staking-dashboard.component.html',
  styleUrl: './staking-dashboard.component.css'
})
export class StakingDashboardComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  router = inject(Router);
  stakingService = inject(StakingService);
  web3Service = inject(Web3Service);
  toastService = inject(ToastService);
  modalService = inject(ModalService);

  poolId = signal<number>(0);
  poolInfo = signal<any>(null);
  isLoading = signal<boolean>(true);
  isPending = signal<boolean>(false);
  
  // State
  stakeAmount: number | null = null;
  userBalance = signal<number>(0);
  
  // Multiple Stakes
  userStakes = signal<StakeInfo[]>([]);
  showStakesModal = signal<boolean>(false);

  // Computed totals for summary card
  get totalStakedAmount(): number {
    return this.userStakes().reduce((acc, s) => acc + Number(s.amount), 0);
  }

  get totalPendingRewards(): number {
    return this.userStakes().reduce((acc, s) => acc + Number(s.pendingRewards), 0);
  }

  private intervalRef?: ReturnType<typeof setInterval>;
  private countdownRef?: ReturnType<typeof setInterval>;

  /**
   * Initializes the controller and sets up an effect to reload user data
   * when the wallet address changes.
   */
  constructor() {
    effect(() => {
      // Reload exactly when wallet connects/disconnects or changes address
      const addr = this.web3Service.userAddress();
      if (addr) {
        this.loadUserStakes();
      } else {
        this.clearUserData();
      }
    });
  }

  /**
   * Angular lifecycle hook. Sets up intervals for automatic reward refreshing
   * and local countdown updates.
   */
  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/']);
      return;
    }
    this.poolId.set(Number(id));
    this.initialLoad();
    
    // Auto-refresh rewards every 15s if staked
    this.intervalRef = setInterval(() => {
      const addr = this.web3Service.userAddress();
      if (addr && this.userStakes().length > 0) {
        this.fetchDynamicRewards(addr as `0x${string}`);
      }
    }, 15000);

    // Local countdown fast-update ticker
    this.countdownRef = setInterval(() => {
      this.userStakes.update(stakes => stakes.map(s => {
        if (!s.isMatured && s.remainingSeconds > 0) {
          const newSeconds = s.remainingSeconds - 1;
          return {
            ...s,
            remainingSeconds: newSeconds,
            formattedCountdown: this.getFormattedTime(newSeconds),
            isMatured: newSeconds <= 0
          };
        }
        return s;
      }));
    }, 1000);
  }

  /**
   * Angular lifecycle hook. Cleans up intervals to prevent memory leaks.
   */
  ngOnDestroy() {
    if (this.intervalRef) clearInterval(this.intervalRef);
    if (this.countdownRef) clearInterval(this.countdownRef);
  }

  /**
   * Performs the initial data load for the dashboard.
   */
  async initialLoad() {
    this.isLoading.set(true);
    await this.loadPoolInfo();
    if (this.web3Service.userAddress()) {
      await this.loadUserStakes();
    }
    this.isLoading.set(false);
  }

  /**
   * Loads only the global pool information.
   */
  async loadPoolInfo() {
    try {
      const pId = BigInt(this.poolId());
      const pInfo = await this.stakingService.readStakingContract('getPoolInfo', [pId]);
      if (pInfo) {
        this.poolInfo.set({
          maturityPeriod: pInfo.maturityPeriod ?? pInfo[0],
          apy: pInfo.apy ?? pInfo[1],
          totalStaked: pInfo.totalStaked ?? pInfo[2],
          isActive: pInfo.isActive ?? pInfo[7],
          stakingEndTime: pInfo.stakingEndTime ?? pInfo[10]
        });
      }
    } catch (e) {
      console.error('Error loading pool info', e);
    }
  }

  /**
   * Loads user-specific stake data from the blockchain.
   */
  async loadUserStakes() {
    const address = this.web3Service.userAddress() as `0x${string}`;
    if (!address) {
      this.clearUserData();
      return;
    }

    try {
      const pId = BigInt(this.poolId());
      
      // 1. Get Stake Count
      const countObj = await this.stakingService.readStakingContract('getUserStakesCount', [address, pId]);
      const count = Number(countObj || 0);
      
      if (count === 0) {
        this.userStakes.set([]);
        return;
      }

      // 2. Fetch all user stakes in ONE multicall
      const batch: { functionName: string; args: any[] }[] = [];
      for (let i = 0; i < count; i++) {
        batch.push({ functionName: 'getUserStake', args: [address, pId, BigInt(i)] });
      }
      const stakeResults = await this.stakingService.multicallStaking(batch);

      // 3. Filter active stakes and build detail batch
      const detailBatch: { functionName: string; args: any[] }[] = [];
      const detailMeta: { index: number; stAmount: bigint }[] = [];

      stakeResults.forEach((res, i) => {
        const info = res?.result;
        if (!info) return;
        const stAmount = (info.amount ?? info[0] ?? 0n) as bigint;
        if (stAmount > 0n) {
          const stakeIdx = BigInt(i);
          detailBatch.push({ functionName: 'pendingRewards',    args: [address, pId, stakeIdx] });
          detailBatch.push({ functionName: 'getMaturityStatus', args: [address, pId, stakeIdx] });
          detailBatch.push({ functionName: 'getPenaltyInfo',    args: [address, pId, stakeIdx] });
          detailMeta.push({ index: i, stAmount });
        }
      });

      if (detailBatch.length === 0) {
        this.userStakes.set([]);
        return;
      }

      // 4. Fetch rewards, maturity, and penalty in ONE multicall
      const detailResults = await this.stakingService.multicallStaking(detailBatch);
      const loadedStakes: StakeInfo[] = [];

      detailMeta.forEach((meta, i) => {
        const pending   = (detailResults[i * 3]?.result || 0n) as bigint;
        const maturity  = detailResults[i * 3 + 1]?.result;
        const penalty   = detailResults[i * 3 + 2]?.result;

        const remainingSeconds = Number(maturity?.remainingTime ?? maturity?.[0] ?? 0);
        const isMatured = !!(maturity?.isMatured ?? maturity?.[1] ?? false);

        loadedStakes.push({
          index: meta.index,
          amount: formatUnits(meta.stAmount, 18),
          pendingRewards: formatUnits(pending, 18),
          isMatured: isMatured,
          remainingSeconds: remainingSeconds,
          formattedCountdown: this.getFormattedTime(remainingSeconds),
          progressPercent: isMatured ? 100 : 50,
          penaltyPercent: Number(penalty?.penaltyPercent ?? penalty?.[0] ?? 0),
          penaltyAmount: formatUnits(penalty?.penaltyAmount ?? penalty?.[1] ?? 0n, 18)
        });
      });

      this.userStakes.set(loadedStakes);
      if (loadedStakes.length === 0 && this.showStakesModal()) {
        this.showStakesModal.set(false);
      }

    } catch (e) {
      console.error('Error loading user stakes', e);
    }
  }

  async loadUserData() {
    // Legacy support or fallback
    await this.loadPoolInfo();
    await this.loadUserStakes();
  }

  /**
   * Periodically refreshes rewards and maturity status for all user stakes.
   * @param address The user's wallet address.
   */
  async fetchDynamicRewards(address: `0x${string}`) {
    try {
      const pId = BigInt(this.poolId());
      const currentStakes = this.userStakes();
      
      const promises = currentStakes.map(async (s) => {
        const stakeIdx = BigInt(s.index);
        try {
          const [pendingObj, maturityObj, penaltyObj] = await Promise.all([
            this.stakingService.readStakingContract('pendingRewards', [address, pId, stakeIdx]),
            this.stakingService.readStakingContract('getMaturityStatus', [address, pId, stakeIdx]),
            this.stakingService.readStakingContract('getPenaltyInfo', [address, pId, stakeIdx])
          ]);

          const isMatured = maturityObj?.isMatured ?? maturityObj?.[1] ?? false;
          const remainingSeconds = Number(maturityObj?.remainingTime ?? maturityObj?.[0] ?? 0);

          return {
            ...s,
            pendingRewards: formatUnits(pendingObj || 0n, 18),
            isMatured: isMatured,
            remainingSeconds: remainingSeconds,
            formattedCountdown: this.getFormattedTime(remainingSeconds),
            progressPercent: isMatured ? 100 : 50,
            penaltyPercent: Number(penaltyObj?.penaltyPercent ?? penaltyObj?.[0] ?? 0),
            penaltyAmount: formatUnits(penaltyObj?.penaltyAmount ?? penaltyObj?.[1] ?? 0n, 18)
          };
        } catch (err) {
          return s; // return unchanged on error
        }
      });

      const updatedStakes = await Promise.all(promises);
      this.userStakes.set(updatedStakes);
      
    } catch (e) {
      console.error('[StakingDashboard] Error in fetchDynamicRewards:', e);
    }
  }

  /**
   * Resets user-related state when the wallet is disconnected.
   */
  clearUserData() {
    this.userBalance.set(0);
    this.userStakes.set([]);
    this.stakeAmount = null;
  }

  /**
   * Formats a duration in seconds into a human-readable string (e.g., "1d 02h 05m 10s").
   * @param totalSeconds The total number of seconds.
   * @returns A formatted time string.
   */
  getFormattedTime(totalSeconds: number): string {
    if (totalSeconds <= 0) return 'Matured';
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    
    let timeStr = '';
    if (d > 0) timeStr += `${d}d `;
    if (h > 0 || d > 0) timeStr += `${h.toString().padStart(2, '0')}h `;
    if (m > 0 || h > 0 || d > 0) timeStr += `${m.toString().padStart(2, '0')}m `;
    timeStr += `${s.toString().padStart(2, '0')}s`;
    
    return timeStr;
  }

  /**
   * Sets the stake input amount to the user's maximum available balance.
   */
  setMax() {
    this.stakeAmount = this.userBalance();
  }

  /**
   * Initiates the staking process for the entered amount.
   */
  async handleStake() {
    if (!this.stakeAmount || this.stakeAmount <= 0) return;
    this.isPending.set(true);
    try {
      const parsedAmount = parseUnits(this.stakeAmount.toString(), 18);
      await this.stakingService.doStake(BigInt(this.poolId()), parsedAmount);
      this.stakeAmount = null;
      this.toastService.success('Tokens STAKED successfully!');
      await this.loadUserStakes();
      // Auto-open modal or show success feedback could be added here
    } catch (e) {
      console.error(e);
      this.toastService.error('Staking failed: User rejected or protocol error.');
    } finally {
      this.isPending.set(false);
    }
  }

  /**
   * Initiates the unstaking process for a specific stake index.
   * @param stakeIndex The index of the stake to unstake.
   */
  async handleUnstake(stakeIndex: number) {
    const stake = this.userStakes().find(s => s.index === stakeIndex);
    if (!stake) return;

    if (!stake.isMatured && stake.penaltyPercent > 0) {
      const confirm = await this.modalService.confirm(
        'Early Unstake Warning',
        `WARNING: You are unstaking early. You will incur a ${stake.penaltyPercent}% penalty and lose all pending rewards. Are you absolutely sure?`,
        'Yes, Unstake',
        'Cancel'
      );
      if (!confirm) return;
    }
    
    this.isPending.set(true);
    try {
      await this.stakingService.writeStakingContract('unstake', [BigInt(this.poolId()), BigInt(stakeIndex)]);
      this.toastService.success('Tokens UNSTAKED successfully!');
      await this.loadUserStakes();
    } catch (e) {
      console.error(e);
      this.toastService.error('Unstaking failed: User rejected or protocol error.');
    } finally {
      this.isPending.set(false);
    }
  }

  /**
   * Initiates the reward claiming process for a specific stake index.
   * @param stakeIndex The index of the stake to claim rewards from.
   */
  async handleClaim(stakeIndex: number) {
    this.isPending.set(true);
    try {
      await this.stakingService.writeStakingContract('claimRewards', [BigInt(this.poolId()), BigInt(stakeIndex)]);
      this.toastService.success('Rewards CLAIMED successfully!');
      await this.loadUserStakes();
    } catch (e) {
      console.error(e);
      this.toastService.error('Claim failed: User rejected or protocol error.');
    } finally {
      this.isPending.set(false);
    }
  }

  /**
   * Toggles the visibility of the detailed stakes modal.
   */
  toggleStakesModal() {
    if (this.userStakes().length === 0) return;
    this.showStakesModal.update(v => !v);
  }
}
